import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { Message, User, CaptionMode } from '../../../types/messaging';
import { STATIC_BASE_URL } from '../../../config';
import { useVoicePlaybackQueue } from '../hooks/useVoicePlaybackQueue';
import { ClickableText } from '../../../components/ClickableWord';
import { chatbotService } from '../../../services/chatbotService';
import { useMessagingStore } from '../../../stores/messagingStore';
import { rearmVoiceOutbox, deleteVoiceOutbox } from '../utils/voiceOutbox';

interface Props {
    message: Message;
    isOwn: boolean;
    showAvatar?: boolean;
    otherParticipant?: User;
    captionMode: CaptionMode;
    /** True when this bubble's audio is the most-recently-arrived bot reply
     *  and should auto-play. Parent computes this. */
    autoPlay?: boolean;
}

/**
 * VoiceMessageBubble — bubble for voice-mode messages. Renders an audio
 * player with progress bar + caption that respects the global CaptionMode:
 *   - 'always'        → caption visible
 *   - 'while_playing' → caption fades in while audio plays
 *   - 'never'         → caption hidden (long-press bubble to reveal)
 *
 * User-side bubbles always show their transcript regardless of CaptionMode
 * (so the learner can verify what Whisper heard).
 */
const VoiceMessageBubble: React.FC<Props> = ({
    message,
    isOwn,
    showAvatar = false,
    otherParticipant,
    captionMode,
    autoPlay = false,
}) => {
    const { t } = useTranslation('messaging');
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const rafRef = useRef<number | null>(null);
    /** True only when this bubble's currently-playing audio was started by
     *  the autoplay queue (not by the user clicking play). Lets cleanup
     *  pause the audio when a newer message takes over, without yanking
     *  the rug out from under a user-initiated playback. */
    const queueDidStartRef = useRef(false);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [forceCaption, setForceCaption] = useState(false);
    const queue = useVoicePlaybackQueue();

    const time = format(new Date(message.createdAt), 'HH:mm');
    const displayName =
        message.sender?.fullName ||
        message.sender?.username ||
        otherParticipant?.fullName ||
        otherParticipant?.username ||
        '';
    const avatarUrl = message.sender?.avatarUrl || otherParticipant?.avatarUrl;
    const initial = displayName.charAt(0).toUpperCase();

    const audioSrc = message.audioUrl
        ? message.audioUrl.startsWith('http')
            ? message.audioUrl
            : `${STATIC_BASE_URL}${message.audioUrl.startsWith('/') ? '' : '/'}${message.audioUrl}`
        : null;

    const meta = useMemo(
        () => (message.metadata ?? {}) as Record<string, unknown>,
        [message.metadata],
    );
    const isNudge = meta.isNudge === true;
    const isTranscribing = meta.isTranscribing === true;
    const detectedLanguage = (meta.detectedLanguage as string | undefined) ?? null;
    const countedTowardQuota = meta.countedTowardQuota !== false;
    const regenAttempts = typeof meta.regenAttempts === 'number' ? meta.regenAttempts : 0;
    const queueState = meta.queueState as 'sending' | 'retrying' | 'failed' | undefined;
    const queueAttempts = typeof meta.attempts === 'number' ? meta.attempts : 0;
    const isQueueRetrying = queueState === 'retrying';
    const isQueueDead = queueState === 'failed';

    // A bot bubble with text but no audioUrl means TTS failed at send-time.
    // Offer a one-click resynth — bot messages only (user bubbles have no
    // server-side audio to regen).
    const canRegenerateAudio =
        !isOwn && !isTranscribing && !audioSrc && !!message.content?.trim();
    const [regenState, setRegenState] = useState<'idle' | 'pending' | 'error'>('idle');
    const regenLimitReached = regenAttempts >= 3;

    const handleManualRetry = useCallback(async () => {
        // Re-arm the dead outbox record so the scheduler picks it up on
        // its next tick. Bubble flips back to "retrying" until the upload
        // resolves one way or the other.
        try {
            await rearmVoiceOutbox(message.id);
            useMessagingStore.getState().updateLocalMessage(message.id, {
                metadata: { ...meta, queueState: 'retrying', attempts: 0, lastError: null },
            });
        } catch (err) {
            console.warn('[voice-outbox] manual retry failed', err);
        }
    }, [message.id, meta]);

    const handleDiscard = useCallback(async () => {
        try {
            await deleteVoiceOutbox(message.id);
        } catch {
            // Best effort — the bubble removal below is what the user actually sees.
        }
        useMessagingStore.getState().removeLocalMessage(message.id);
    }, [message.id]);

    const handleRegenerate = useCallback(async () => {
        if (regenState === 'pending' || regenLimitReached) return;
        setRegenState('pending');
        try {
            const res = await chatbotService.regenerateBotVoice(message.id);
            if (res.ok && res.audioUrl) {
                useMessagingStore.getState().updateLocalMessage(message.id, {
                    audioUrl: res.audioUrl,
                    audioDurationMs: res.audioDurationMs ?? null,
                    metadata: { ...meta, regenAttempts: regenAttempts + 1 },
                });
                setRegenState('idle');
            } else {
                setRegenState('error');
            }
        } catch {
            setRegenState('error');
        }
    }, [message.id, meta, regenAttempts, regenLimitReached, regenState]);

    // Words the bot explicitly taught this turn — split multi-word phrases
    // into individual tokens because ClickableText highlights at word grain.
    const taughtTermsRaw = Array.isArray(meta.taughtTerms)
        ? (meta.taughtTerms as string[])
        : [];
    const taughtTerms = taughtTermsRaw.flatMap((t) =>
        t.split(/\s+/).filter((w) => w.length > 0),
    );

    useEffect(() => {
        if (!autoPlay || !audioSrc) return;
        const messageId = message.id;

        if (queue) {
            queue.enqueueAutoplay({
                messageId,
                play: async () => {
                    const a = audioRef.current;
                    if (!a) throw new Error('audio element unmounted');
                    queueDidStartRef.current = true;
                    await a.play();
                },
            });

            const a = audioRef.current;
            const onEnded = () => {
                queueDidStartRef.current = false;
                queue.releaseLock(messageId);
            };
            a?.addEventListener('ended', onEnded);
            return () => {
                a?.removeEventListener('ended', onEnded);
                queue.releaseLock(messageId);
                // If autoplay just flipped off because a newer message
                // arrived, stop this audio so the new one can take over
                // without overlapping. Only do this for queue-started
                // playback — never interrupt a user's manual play.
                if (queueDidStartRef.current && a && !a.paused) {
                    a.pause();
                    a.currentTime = 0;
                }
                queueDidStartRef.current = false;
            };
        }

        // Provider not mounted — fall back to direct autoplay (single bubble).
        const id = setTimeout(() => {
            audioRef.current?.play().catch(() => undefined);
        }, 80);
        return () => clearTimeout(id);
    }, [autoPlay, audioSrc, message.id, queue]);

    const showCaption = (() => {
        // User-side: always show transcript (verification).
        if (isOwn) return true;
        if (captionMode === 'always') return true;
        if (captionMode === 'while_playing' && playing) return true;
        return forceCaption;
    })();

    const onLongPress = () => {
        if (!isOwn && captionMode !== 'always') {
            setForceCaption(true);
            window.setTimeout(() => setForceCaption(false), 5000);
        }
    };

    const togglePlay = () => {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play();
        else a.pause();
    };

    // Drive the progress bar via requestAnimationFrame instead of `timeupdate`.
    // `timeupdate` fires ~4Hz which combined with a CSS transition makes the
    // bar visibly stutter. rAF reads currentTime every frame, so the bar
    // tracks the playhead smoothly without any transition.
    useEffect(() => {
        if (!playing) {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            return;
        }
        const tick = () => {
            const a = audioRef.current;
            if (a && a.duration > 0) {
                setProgress(a.currentTime / a.duration);
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [playing]);

    const totalSec = message.audioDurationMs ? message.audioDurationMs / 1000 : null;

    return (
        <div
            className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-1`}
            data-testid="voice-bubble"
            data-is-own={isOwn ? 'true' : 'false'}
        >
            <div className={`flex items-start gap-2 max-w-[85%] ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                {!isOwn && (
                    <div className="flex-shrink-0 w-8">
                        {showAvatar ? (
                            avatarUrl ? (
                                <img src={avatarUrl} alt={displayName} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 text-sm font-semibold">
                                    {initial}
                                </div>
                            )
                        ) : (
                            <div className="w-8 h-8" />
                        )}
                    </div>
                )}

                <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                    <div
                        className={`
                            px-4 py-3 rounded-2xl
                            ${isOwn
                                ? 'bg-primary-500 text-white rounded-br-md'
                                : 'bg-gray-100 text-gray-900 rounded-bl-md'
                            }
                        `}
                        onContextMenu={(e) => { e.preventDefault(); onLongPress(); }}
                        data-testid="voice-bubble-body"
                    >
                        {audioSrc ? (
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={togglePlay}
                                    className={`
                                        flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center
                                        ${isOwn ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-primary-500 hover:bg-primary-600 text-white'}
                                    `}
                                    aria-label={playing ? t('voice.pause', 'Pause') : t('voice.play', 'Play')}
                                    data-testid="voice-play-button"
                                >
                                    {playing ? (
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                                    )}
                                </button>

                                <div className="flex-1 min-w-[120px]">
                                    <div className={`h-1.5 rounded-full ${isOwn ? 'bg-white/30' : 'bg-gray-300'}`}>
                                        <div
                                            className={`h-1.5 rounded-full ${isOwn ? 'bg-white' : 'bg-primary-500'}`}
                                            style={{ width: `${(progress * 100).toFixed(2)}%` }}
                                        />
                                    </div>
                                    {totalSec !== null && (
                                        <span className={`block mt-1 text-[11px] font-mono ${isOwn ? 'text-white/80' : 'text-gray-500'}`}>
                                            {totalSec.toFixed(1)}s
                                        </span>
                                    )}
                                </div>

                                <audio
                                    ref={audioRef}
                                    src={audioSrc}
                                    preload="metadata"
                                    onPlay={() => setPlaying(true)}
                                    onPause={() => setPlaying(false)}
                                    onEnded={() => { setPlaying(false); setProgress(1); }}
                                />
                            </div>
                        ) : isTranscribing ? (
                            <div
                                className="flex items-center gap-2 min-h-[1.25rem]"
                                aria-label={t('voice.transcribing', 'Transcribing...')}
                                data-testid="voice-transcribing"
                            >
                                <span className={`flex gap-1 ${isOwn ? 'text-white/80' : 'text-gray-500'}`}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
                                </span>
                                <span className={`text-xs ${isOwn ? 'text-white/80' : 'text-gray-500'}`}>
                                    {t('voice.transcribing', 'Transcribing...')}
                                </span>
                            </div>
                        ) : isQueueRetrying ? (
                            <div
                                className="flex items-center gap-2 min-h-[1.25rem]"
                                data-testid="voice-queue-retrying"
                            >
                                <span
                                    className={`w-3 h-3 border ${isOwn ? 'border-white/60 border-t-transparent' : 'border-gray-400 border-t-transparent'} rounded-full animate-spin`}
                                    aria-hidden="true"
                                />
                                <span className={`text-xs ${isOwn ? 'text-white/80' : 'text-gray-500'}`}>
                                    {queueAttempts > 0
                                        ? t('voice.queueRetryingN', 'Reconnecting… (attempt {{n}})', { n: queueAttempts })
                                        : t('voice.queueQueued', 'Queued — sending when online')}
                                </span>
                            </div>
                        ) : isQueueDead ? (
                            <div className="flex flex-col gap-2" data-testid="voice-queue-failed">
                                <span className={`text-xs ${isOwn ? 'text-white/90' : 'text-red-700'}`}>
                                    {t('voice.queueFailed', 'Couldn\'t send — still here, your call')}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleManualRetry}
                                        className={`
                                            inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full
                                            ${isOwn ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}
                                        `}
                                        data-testid="voice-queue-retry"
                                    >
                                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M3 12a9 9 0 1 0 3-6.7" />
                                            <polyline points="3 4 3 10 9 10" />
                                        </svg>
                                        {t('voice.queueRetryAction', 'Retry')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDiscard}
                                        className={`
                                            inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full
                                            ${isOwn ? 'bg-white/10 text-white/90 hover:bg-white/20' : 'text-gray-500 hover:bg-gray-100'}
                                        `}
                                        data-testid="voice-queue-discard"
                                    >
                                        {t('voice.queueDiscardAction', 'Discard')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <p className={`text-sm whitespace-pre-wrap ${isOwn ? 'text-white' : 'text-gray-900'}`}>
                                    <ClickableText text={message.content} highlightWords={taughtTerms} />
                                </p>
                                {canRegenerateAudio && (
                                    <button
                                        type="button"
                                        onClick={handleRegenerate}
                                        disabled={regenState === 'pending' || regenLimitReached}
                                        className={`
                                            self-start inline-flex items-center gap-1.5 px-2.5 py-1
                                            text-xs rounded-full border transition-colors
                                            ${regenLimitReached
                                                ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                                : regenState === 'error'
                                                    ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }
                                            disabled:opacity-60 disabled:cursor-wait
                                        `}
                                        data-testid="voice-regen-button"
                                    >
                                        {regenState === 'pending' ? (
                                            <>
                                                <span
                                                    className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"
                                                    aria-hidden="true"
                                                />
                                                {t('voice.regenerating', 'Generating audio…')}
                                            </>
                                        ) : (
                                            <>
                                                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M3 12a9 9 0 1 0 3-6.7" />
                                                    <polyline points="3 4 3 10 9 10" />
                                                </svg>
                                                {regenLimitReached
                                                    ? t('voice.regenLimit', 'Audio unavailable')
                                                    : regenState === 'error'
                                                        ? t('voice.regenRetry', 'Retry audio')
                                                        : t('voice.regenAudio', 'Generate audio')}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        )}

                        {showCaption && audioSrc && message.content && (
                            <p
                                className={`mt-2 text-sm ${isOwn ? 'text-white/95' : 'text-gray-700'}`}
                                data-testid="voice-caption"
                            >
                                <ClickableText text={message.content} highlightWords={taughtTerms} />
                            </p>
                        )}
                    </div>

                    {!isTranscribing && (isNudge || (isOwn && countedTowardQuota === false)) && (
                        <div className="mt-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-[11px] text-amber-700">
                            {isOwn
                                ? t('voice.nudgeChip', 'Try in English — this turn doesn\'t count')
                                : t('voice.nudgeBotChip', `Detected ${detectedLanguage ?? 'non-English'} — kept the meter paused`)}
                        </div>
                    )}

                    <div className={`flex items-center gap-1 mt-1 px-1 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                        <span className="text-xs text-gray-400">{time}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VoiceMessageBubble;
